// Modèle du bilan de sauvegarde (revue du 2026-09-15, complétée le 2026-09-16).
// Extrait de `MapWithMarker.js` pour être TESTABLE : la règle métier — ce qu'on
// peut affirmer à l'utilisateur sur chaque opération — ne doit pas être noyée
// dans un composant, sinon elle ne peut être vérifiée que par une recette
// manuelle.

/**
 * Issue d'une opération d'écriture, du point de vue du CLIENT.
 *
 * - `confirmed` : la réponse du serveur a été reçue et acceptée.
 * - `refused`   : le serveur a répondu explicitement un refus (4xx contractuel).
 *                 Seul ce cas autorise à affirmer « n'a pas été enregistré ».
 * - `unknown`   : la requête est partie mais AUCUNE réponse exploitable n'est
 *                 revenue (coupure réseau, réponse perdue, délai dépassé).
 *                 L'écriture a PU aboutir : ne jamais l'annoncer comme un échec.
 * - `notAttempted` : l'opération n'a jamais été envoyée.
 */
export const SAVE_OUTCOMES = Object.freeze({
  confirmed: "confirmed",
  refused: "refused",
  unknown: "unknown",
  notAttempted: "notAttempted",
});

export const getSaveRefusalKey = (originMarker, destinationMarker, markers) => {
  const hasOrigin =
    (originMarker && Object.keys(originMarker).length > 0) ||
    markers.some((marker) => marker.type === "origin");
  const hasDestination =
    (destinationMarker && Object.keys(destinationMarker).length > 0) ||
    markers.some((marker) => marker.type === "destination");
  if (!hasOrigin && !hasDestination) return "saveNeedsStartAndEnd";
  if (!hasOrigin) return "saveNeedsStart";
  if (!hasDestination) return "saveNeedsEnd";
  return null;
};

export const SAVE_REPORT_KEYS = Object.freeze({
  track: "partialSaveTrackMissing",
  trackUnknown: "partialSaveTrackUnknown",
  media: "partialSaveMediaMissing",
  mediaUnknown: "partialSaveMediaUnknown",
  mediaCleanup: "partialSaveMediaCleanup",
  poi: "partialSavePoiMissing",
  poiUnknown: "partialSavePoiUnknown",
  message: "partialSaveMessage",
  other: "partialSaveOther",
  // Conseil affiché SEULEMENT lorsqu'il est sans danger : il ne doit jamais
  // figurer dans le même bilan qu'un fichier encore présent dans la mémoire du
  // navigateur, car un rechargement le détruirait.
  reloadAdvice: "partialSaveReloadCheck",
});

// Ordre d'apparition dans le bilan combiné : le tracé d'abord (donnée lourde,
// écrasée si l'utilisateur recharge en croyant avoir sauvegardé), puis les
// MÉDIAS (fichiers choisis par l'utilisateur, définitivement perdus au
// rechargement de la page car la file d'attente est en mémoire), puis le reste
// (points d'intérêt, préférences).
export const SAVE_REPORT_ORDER = Object.freeze([
  SAVE_REPORT_KEYS.track,
  SAVE_REPORT_KEYS.trackUnknown,
  SAVE_REPORT_KEYS.media,
  SAVE_REPORT_KEYS.mediaUnknown,
  SAVE_REPORT_KEYS.poi,
  SAVE_REPORT_KEYS.poiUnknown,
  SAVE_REPORT_KEYS.mediaCleanup,
  SAVE_REPORT_KEYS.message,
  SAVE_REPORT_KEYS.other,
]);

// Regroupements par donnée concernée : le tracé (une seule donnée, écrasable)
// et les médias (fichiers de l'utilisateur, en mémoire du navigateur).
const TRACK_KEYS = new Set([
  SAVE_REPORT_KEYS.track,
  SAVE_REPORT_KEYS.trackUnknown,
]);
const MEDIA_KEYS = new Set([
  SAVE_REPORT_KEYS.media,
  SAVE_REPORT_KEYS.mediaUnknown,
  SAVE_REPORT_KEYS.mediaCleanup,
]);

// Catégories qui prouvent qu'un fichier choisi par l'utilisateur n'existe QUE
// dans la mémoire du navigateur. Un rechargement de page le perd
// définitivement : leur présence interdit tout conseil de rechargement.
const PENDING_FILE_KEYS = new Set([
  SAVE_REPORT_KEYS.media,
  SAVE_REPORT_KEYS.mediaUnknown,
]);

// Catégories où l'écriture a PU aboutir sans confirmation : c'est la seule
// situation où une vérification du serveur a un sens.
const UNKNOWN_WRITE_KEYS = new Set([
  SAVE_REPORT_KEYS.trackUnknown,
  SAVE_REPORT_KEYS.mediaUnknown,
  SAVE_REPORT_KEYS.poiUnknown,
]);

// Normalise l'issue d'une opération. Un appelant qui n'a qu'un booléen
// (`true`/`false`) reste exploitable — `false` est lu comme un refus certain,
// ce qui correspond au seul cas alors connu — mais le modèle complet permet de
// distinguer le refus du silence réseau.
export const normalizeOutcome = (outcome) => {
  if (typeof outcome === "boolean") {
    return outcome ? SAVE_OUTCOMES.confirmed : SAVE_OUTCOMES.refused;
  }
  return Object.values(SAVE_OUTCOMES).includes(outcome)
    ? outcome
    : SAVE_OUTCOMES.unknown;
};

/**
 * Bilan d'une sauvegarde, composé à partir de TOUTES les catégories d'échec.
 *
 * Le défaut d'origine était à la fois binaire (un booléen de succès pour le
 * tracé) et exclusif (la première catégorie gagnante masquait les autres) :
 * un PUT refusé faisait disparaître l'avertissement sur les fichiers restés en
 * mémoire, et une réponse perdue après écriture était annoncée comme un échec
 * certain de l'enregistrement.
 *
 * Renvoie `null` s'il n'y a réellement rien à signaler — l'appelant ne doit
 * jamais afficher de bilan vide.
 *
 * @param {object} report
 * @param {string|boolean} report.trackOutcome Issue du PUT du tracé.
 * @param {Array} report.failures Échecs unitaires `{ scope, category, kind }`.
 * @returns {{ keys: string[], track: string, pendingUploads: boolean }|null}
 */
export const buildSaveReport = ({ trackOutcome, failures } = {}) => {
  const list = failures || [];
  const outcome = normalizeOutcome(trackOutcome);
  const keys = new Set();

  if (outcome === SAVE_OUTCOMES.refused) {
    keys.add(SAVE_REPORT_KEYS.track);
  } else if (outcome === SAVE_OUTCOMES.unknown) {
    // Ne JAMAIS présenter une réponse perdue comme la preuve d'une absence
    // d'écriture : le serveur a pu persister avant la coupure.
    keys.add(SAVE_REPORT_KEYS.trackUnknown);
  }
  for (const failure of list) {
    // Entrée incomplète : on ne peut pas la classer, mais sa présence prouve
    // qu'une opération a échoué — se taire serait pire que le message
    // générique.
    if (!failure) {
      keys.add(SAVE_REPORT_KEYS.other);
      continue;
    }
    switch (failure.kind || failure.category) {
      case "track":
        // Échec du PUT du tracé : déjà porté par `trackOutcome`, qui seul sait
        // si le serveur a refusé ou si la réponse s'est perdue. Ne pas le
        // compter deux fois (et ne jamais en déduire un échec média).
        break;
      case "upload":
        // Envoi média NON CONFIRMÉ. `uploadUnknown` (réponse perdue après
        // persistance possible) impose la formulation prudente : le fichier a
        // pu atteindre le serveur. Les deux cas laissent cependant le fichier
        // en file en mémoire, donc un rechargement reste destructeur.
        keys.add(
          failure.uploadUnknown
            ? SAVE_REPORT_KEYS.mediaUnknown
            : SAVE_REPORT_KEYS.media
        );
        break;
      case "uploadUnknown":
        // CONTRAT PRODUCTEUR (contre-validation du 16 septembre, Spec 1) :
        // l'orchestration classify les échecs de flush via
        // `classifyFlushFailure`, qui rend le kind « uploadUnknown » — et non
        // `{kind:"upload", uploadUnknown:true}`. Les DEUX formes désignent le
        // même fait (envoi d'issue inconnue) et doivent produire la même clé :
        // ne reconnaître que la seconde faisait tomber la première dans
        // « autre », d'où un conseil de rechargement malgré un fichier resté
        // en mémoire.
        keys.add(SAVE_REPORT_KEYS.mediaUnknown);
        break;
      case "pendingMedia":
        // Le POI n'a pas pu être enregistré : ses fichiers ne sont donc même
        // pas partis et restent UNIQUEMENT dans la mémoire du navigateur. La
        // file d'attente étant en mémoire, un rechargement les perd.
        keys.add(SAVE_REPORT_KEYS.media);
        break;
      case "replacement":
        // Le nouveau média EST enregistré ; c'est le nettoyage de l'ancien qui
        // reste à faire. Ne jamais annoncer un fichier non envoyé ici.
        keys.add(SAVE_REPORT_KEYS.mediaCleanup);
        break;
      case "poi":
        // Écriture POI refusée explicitement, ou échouée de façon certaine.
        keys.add(SAVE_REPORT_KEYS.poi);
        break;
      case "poiUnknown":
        // Réponse perdue : le POI a pu être créé ou mis à jour. L'annoncer
        // comme un échec certain était le défaut relevé par la revue.
        keys.add(SAVE_REPORT_KEYS.poiUnknown);
        break;
      case "message":
        keys.add(SAVE_REPORT_KEYS.message);
        break;
      case "media":
        // Catégorie historique (avant la distinction upload/nettoyage) : on
        // ne peut pas affirmer que le fichier n'a pas été envoyé, donc on
        // reste sur l'avertissement le plus prudent.
        keys.add(SAVE_REPORT_KEYS.media);
        break;
      default:
        keys.add(SAVE_REPORT_KEYS.other);
    }
  }

  if (keys.size === 0) return null;

  // CONSEIL DE RECHARGEMENT : indicateur SÉPARÉ, ajouté UNIQUEMENT s'il ne
  // peut rien détruire. Le texte d'origine conseillait Ctrl+Maj+R sans
  // condition ; si le même bilan signalait un fichier resté en mémoire, suivre
  // ce conseil supprimait le travail de l'utilisateur. Règle : un rechargement
  // n'est proposé que lorsqu'au moins une écriture est d'issue INCONNUE (sa
  // vérification a un sens) ET qu'AUCUN fichier n'attend en mémoire du
  // navigateur. Volontairement hors de `keys` : ce n'est pas une catégorie
  // d'échec, et il ne doit pas déclencher l'en-tête « plusieurs éléments ».
  const hasPendingFiles = [...keys].some((key) => PENDING_FILE_KEYS.has(key));
  const hasUnknownWrite = [...keys].some((key) =>
    UNKNOWN_WRITE_KEYS.has(key)
  );

  const ordered = SAVE_REPORT_ORDER.filter((key) => keys.has(key));
  const single = ordered.length === 1 ? ordered[0] : null;
  return {
    keys: ordered,
    // Clé unique (contrat historique) ou clé d'en-tête du message composé.
    track: single || SAVE_REPORT_KEYS.message,
    // Vrai si des fichiers ne sont QUE dans la mémoire du navigateur : le
    // message doit alors interdire de fermer ou recharger la page.
    pendingUploads: hasPendingFiles,
    // Vrai seulement quand un rechargement est sans danger (vérification
    // d'issue inconnue, aucun fichier en attente).
    reloadAdvice: hasUnknownWrite && !hasPendingFiles,
  };
};

// Compatibilité : `pickSaveReportMessage` reste le point d'entrée du texte
// principal. Il ne rend plus qu'un message par situation — la composition
// multi-catégories passe par `buildSaveReport(...).keys`.
export const pickSaveReportMessage = (trackSaved, failures) =>
  buildSaveReport({ trackOutcome: trackSaved, failures })?.track ?? null;

// Indique si des fichiers ne sont présents que dans la mémoire du navigateur.
// Utilisé pour décider d'un délai d'affichage plus long sur le bilan.
export const saveReportHasPendingUploads = (report) =>
  Boolean(report && report.pendingUploads);

// Résumé journalisable (jamais affiché à l'utilisateur) : le nombre et la
// portée des échecs, pour diagnostiquer sans exposer le corps d'une réponse
// serveur ni empiler un message par marqueur.
export const summarizeSaveFailures = (failures) => {
  const list = failures || [];
  return {
    count: list.length,
    scopes: list.map((failure) => (failure && failure.scope) || "inconnu"),
  };
};

// Nature d'un échec de flush média : un ENVOI raté (le fichier n'a jamais
// atteint le serveur et n'existe que dans la mémoire du navigateur) ou le
// NETTOYAGE raté d'un média remplacé (le nouveau média est publié, l'ancien
// reste à supprimer). Le composant ne doit plus réduire les deux à « média ».
//
// L'envoi raté est en outre subdivisé : un REFUS explicite du serveur (4xx, la
// requête est arrivée) autorise à dire que le fichier n'a pas été envoyé ; une
// RÉPONSE PERDUE (coupure, délai, réponse illisible) ne le permet pas — le
// serveur a pu recevoir le binaire avant la coupure. Confondre les deux était
// le défaut relevé par la revue du 16 septembre.
export const classifyFlushFailure = (failure, isResponseLost) => {
  if (failure && failure.kind === "replacement") {
    return "replacement";
  }
  return isResponseLost ? "uploadUnknown" : "upload";
};

// Erreurs qui prouvent que la requête a pu QUITTER le poste : erreurs de
// transport du navigateur (fetch, abandon, délai) et refus du serveur autres
// que les 4xx contractuels (un 5xx arrive APRÈS réception, l'écriture peut
// avoir été partiellement appliquée).
const TRANSPORT_ERROR_NAMES = new Set([
  "TypeError", // `fetch` : « Failed to fetch » (coupure, DNS, CORS)
  "AbortError", // requête interrompue (délai dépassé côté client)
  "TimeoutError",
  "NetworkError",
]);
const TRANSPORT_ERROR_MESSAGE = /failed to fetch|networkerror|network request failed|load failed|timeout|timed out/i;

// Refus EXPLICITE du serveur : une réponse HTTP 4xx est revenue. C'est le seul
// cas où l'on peut affirmer qu'une écriture n'a pas abouti.
export const isApiRefusal = (error) =>
  Boolean(
    error &&
      error.name === "RoadmakerApiError" &&
      typeof error.status === "number" &&
      error.status >= 400 &&
      error.status < 500
  );

/**
 * Une réponse a-t-elle pu être PERDUE (l'écriture a pu aboutir) ?
 *
 * Trois situations, dans cet ordre :
 *
 * 1. Refus 4xx reçu → NON : rien n'a été écrit, on peut l'affirmer.
 * 2. Erreur de TRANSPORT (fetch, abandon, délai) ou réponse d'un statut
 *    ambigu (5xx : le serveur a reçu la requête avant d'échouer) → OUI.
 * 3. Erreur locale sans aucune trace de contact HTTP (lecture ou chiffrement
 *    d'un fichier échoué avant l'envoi, par exemple) → NON : rien n'est parti.
 *
 * Le cas 3 est une décision explicite : une erreur qui ne porte NI statut HTTP
 * NI marque de transport est produite AVANT l'envoi, donc l'absence de contact
 * est établie. C'est le seul cas, avec le refus 4xx, où l'on peut dire que
 * l'écriture n'a pas eu lieu.
 */
export const isResponseLostFailure = (error) => {
  if (isApiRefusal(error)) {
    return false;
  }
  if (error && typeof error.status === "number") {
    return true;
  }
  if (error && TRANSPORT_ERROR_NAMES.has(error.name)) {
    return true;
  }
  return Boolean(
    error &&
      typeof error.message === "string" &&
      TRANSPORT_ERROR_MESSAGE.test(error.message)
  );
};

// Interrogateurs utilisés par les tests et par l'affichage pour regrouper les
// avertissements qui concernent la même donnée.
export const isTrackKey = (key) => TRACK_KEYS.has(key);
export const isMediaKey = (key) => MEDIA_KEYS.has(key);
// Un rechargement peut détruire un fichier encore présent en mémoire.
export const isPendingFileKey = (key) => PENDING_FILE_KEYS.has(key);
// L'écriture a pu aboutir sans confirmation : à vérifier, jamais à annoncer
// comme un échec certain.
export const isUnknownWriteKey = (key) => UNKNOWN_WRITE_KEYS.has(key);